
import { ReactNode, useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Container, OffcanvasBody, OffcanvasHeader, Row, Table } from "reactstrap";
import styles from "./style.module.scss"
import cx from "classnames";
import DefaultOffCanvas, { DefaultOffCanvasProps } from "../DefaultOffCanvas";
import { OpenSearchClientByIdentificationPubSub, OpenSearchClientByIdentificationPubSubPayload } from "@utils/pubsubs/PubSub";
import { ClientDataType } from "@/api/ClientApi";
import DynamicForm, { DynamicFormRef } from "@mavichpe/dynamic-forms";
import SearchIdentificacionForm from "./form";
import { ActividadEconomicasResponse, HaciendaApi, HaciendaConnectionError } from "@/atv/HaciendaApi";
import { showAlert } from "@utils/Alert";
import LoadingScreen from "@components/content/LoadingScreen";
import { HiddenableContainer } from "@components/layout/HiddenableContainer";
import { EINVOICE_INVOICE_TYPE_CODE, EINVOICE_TICKET_TYPE_CODE } from "@utils/Constants";
import { isTaxpayerRegistered } from "@utils/taxpayerRegistry";

type IdentificationSearchFormData = {
    identificacion: string
}

type SearchClientByIdentificationOffCanvasProps = {
    onClientSelected: (clientData?: ClientDataType) => void
} & DefaultOffCanvasProps

const SearchClientByIdentificationOffCanvas = (props: SearchClientByIdentificationOffCanvasProps) => {
    const { btnColor = "primary", className, onClientSelected } = props;
    const [isOpen, setOpen] = useState(false);
    const formRef = useRef<DynamicFormRef>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [clientIdentification, setClientIdentification] = useState<string>();
    const [clientInformation, setClientInformation] = useState<ActividadEconomicasResponse>();
    const actividadEconomicaPrincipal = clientInformation?.actividades?.find(ae => ae.tipo == "P");
    // Mismo criterio que usa la validacion del padron en los formularios de terceros.
    const clientEstaInscrito = isTaxpayerRegistered(clientInformation?.situacion);

    useEffect(() => {
        const unsubscribe = OpenSearchClientByIdentificationPubSub.subscribe(openOffCanvas);
        return () => {
            unsubscribe();
        };
    }, []);

    const openOffCanvas = async (payload: OpenSearchClientByIdentificationPubSubPayload) => {
        const { openOffCanvas = false } = payload;
        setOpen(openOffCanvas);
    }


    const toogleOffCanvas = (newState: boolean) => {
        setOpen(newState);
        if (newState == false) {
            onCanvasClosed();
        }
    }

    const onCanvasClosed = () => {
        OpenSearchClientByIdentificationPubSub.publish({ openOffCanvas: false })
    }

    const searchClientByIdentificacion = async (formData: IdentificationSearchFormData) => {
        try {
            setIsLoading(true)
            const actividadEconomicas = await HaciendaApi.getActividadesEconomicas(formData.identificacion)
            setClientInformation(actividadEconomicas)
            setClientIdentification(formData.identificacion)
        } catch (err) {
            const message = err instanceof HaciendaConnectionError
                ? err.message
                : "No se encuentra informacion para el numero de identificacion ingresado";
            showAlert("danger", message);
            setClientIdentification("");
            setClientInformation({} as ActividadEconomicasResponse)
        } finally {
            setIsLoading(false)
        }

    }

    const triggerClientSelection = () => {
        let einvoiceKind = clientEstaInscrito ? EINVOICE_INVOICE_TYPE_CODE : EINVOICE_TICKET_TYPE_CODE;

        const client: ClientDataType = {
            nombre: clientInformation?.nombre,
            tipo_identificacion: clientInformation?.tipoIdentificacion,
            identificacion: clientIdentification,
            activitycode: `${actividadEconomicaPrincipal?.codigo || ""}`,
            einvoice_kind: einvoiceKind,
        }
        onClientSelected(client)
        resetForm()
    }
    const resetForm = () => {
        onCanvasClosed()
        formRef.current?.resetFormValue()
        setClientIdentification("");
        setClientInformation({} as ActividadEconomicasResponse)
    }

    const renderSecondaryEconomicActivities = (): ReactNode | null => {
        const secondaryActivities = clientInformation?.actividades?.filter(ae => ae.tipo == "S")
        if (secondaryActivities && secondaryActivities?.length > 0) {
            const heading = (
                <tr className={styles.highlighted}>
                    <td colSpan={2} >Actividad Economica Secundarias</td>
                </tr>
            )
            const activitiesRows: ReactNode[] = []
            secondaryActivities.forEach(ae => {
                const codido = (<tr>
                    <td><b>Codigo:</b></td>
                    <td><b>{ae?.codigo}</b></td>
                </tr>)
                activitiesRows.push(codido)

                const description = (<tr>
                    <td><b>Descripcion:</b></td>
                    <td>{ae?.descripcion}</td>
                </tr>)
                activitiesRows.push(description)
            });
            return [
                heading,
                ...activitiesRows
            ]
        }
    }

    return (
        <>
            {props.children && (<Button
                color={btnColor}
                type="button"
                className={cx([className, styles.button])}
                onClick={() => toogleOffCanvas(!isOpen)}
            >
                {props.children}
            </Button>
            )}
            <DefaultOffCanvas
                className={styles.offCanvas}
                direction="end"
                isOpen={isOpen}
                onClosed={onCanvasClosed}
                toggle={() => toogleOffCanvas(!isOpen)}
            >

                <OffcanvasHeader className={styles.offCanvasHeader} toggle={onCanvasClosed}>
                    Buscar Cliente por Numero de Cedula
                </OffcanvasHeader>
                <OffcanvasBody>
                    <Container>
                        <DynamicForm<IdentificationSearchFormData>
                            ref={formRef}
                            id="statusForm"
                            formConfig={SearchIdentificacionForm}
                            onSubmit={searchClientByIdentificacion}
                        >
                        </DynamicForm>

                        <HiddenableContainer show={!!clientInformation?.nombre && !isLoading}>
                            <Row className="mt-3">
                                <Col>
                                    <Table bordered>
                                        <tbody>
                                            <tr>
                                                <td><b>Nombre:</b></td>
                                                <td>{clientInformation?.nombre}</td>
                                            </tr>
                                            <tr>
                                                <td><b>Inscrito ante Hacienda:</b></td>
                                                <td>{clientInformation?.situacion?.estado}</td>
                                            </tr>
                                            <tr>
                                                <td><b>Tipo de Regimen:</b></td>
                                                <td>{clientInformation?.regimen?.descripcion}</td>
                                            </tr>
                                            {clientEstaInscrito && (
                                                <>
                                                    <tr>
                                                        <td>&nbsp;</td>
                                                        <td>&nbsp;</td>
                                                    </tr>
                                                    <tr className={styles.highlighted}>
                                                        <td colSpan={2} >Actividad Economica Principal</td>
                                                    </tr>
                                                    <tr>
                                                        <td><b>Codigo:</b></td>
                                                        <td>{actividadEconomicaPrincipal?.codigo}</td>
                                                    </tr>
                                                    <tr>
                                                        <td><b>Descripcion:</b></td>
                                                        <td>{actividadEconomicaPrincipal?.descripcion}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>&nbsp;</td>
                                                        <td>&nbsp;</td>
                                                    </tr>
                                                </>
                                            )}
                                            {renderSecondaryEconomicActivities()}
                                        </tbody>

                                    </Table>
                                </Col>
                            </Row>
                            <Alert color={clientEstaInscrito ? "success" : "warning"}>
                                {
                                    clientEstaInscrito ?
                                        'El cliente esta inscrito ante Hacienda, al agregar este cliente al Sistema se asignara la actividad economica principal y se seleccionara facturacion electronica por defecto'
                                        :
                                        'El cliente no esta inscrito ante Hacienda, al agregar este cliente al Sistema se seleccionara ticket electronica por defecto y no se agregara ningunca actividad economica'
                                }

                            </Alert>
                            <Row className="mt-3">
                                <Col >
                                    <Button color="primary" type="button" onClick={triggerClientSelection}> Agregar Cliente al sistema</Button>
                                </Col>
                            </Row>
                        </HiddenableContainer>
                    </Container>
                </OffcanvasBody>
            </DefaultOffCanvas >

            <LoadingScreen isLoading={isLoading} />
        </>
    );
}
export default SearchClientByIdentificationOffCanvas;
